import { MembershipSettings } from '@/common/models';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import LocalCache from '@/utils/LocalCache';
import NumbroUtils from '@/utils/NumbroUtils';
import i18n, { numberFormats } from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  async updateOrganization({ commit }, organization) {
    commit(types.UPDATE_ORGANIZATION, organization);
    if (organization === null) {
      LocalCache.remove('organization');
      commit(types.UPDATE_ORGANIZATION_SETTINGS, null);
      LocalCache.remove('organizationSettings');
    } else {
      LocalCache.set('organization', organization.toApi());
      LocalCache.set('previousOrganizationType', organization.type);
      // reload themes on organization selection
      API.reset();
      API.instance
        .getTheme()
        .then(async (result) => {
          const theme = result.data;
          const currentLogo = LocalCache.get('headerLogoURL');
          if (theme?.header_logo !== currentLogo) {
            LocalCache.set('headerLogoURL', theme.header_logo);
          }
        })
        .finally(() => {
          GlobalEventBus.$emit(GlobalEvents.updateHeaderLogo);
          GlobalEventBus.$emit(GlobalEvents.fetchUnreadAnnouncements);
        });
      organization.isTTD = await organization.getIsTTD();
    }
  },

  async getPermissions({ commit }, { showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const permissions = await API.instance.getPermissions().catch((err) => {
      const msg = i18n.global.t('messages.failedToGetPermissions');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    commit(types.SET_PERMISSIONS, permissions);
    LocalCache.set('permissions', permissions.toApi());

    return permissions;
  },

  async updatePermissions({ commit }, permissions) {
    commit(types.SET_PERMISSIONS, permissions);
    LocalCache.set('permissions', permissions.toApi());
  },

  async clearPermissions({ commit }) {
    commit(types.SET_PERMISSIONS, null);
    LocalCache.remove('permissions');
  },

  async getOrganizationSettings({ commit, state }) {
    const organizationSettings: MembershipSettings = await API.instance.getOrganizationSettings().catch((err) => {
      const msg = i18n.global.t('messages.failedToGetOrganizationSettings');
      ErrorHandler.showError(msg, err);
      throw err;
    });

    commit(types.UPDATE_ORGANIZATION_SETTINGS, organizationSettings);
    LocalCache.set('organizationSettings', organizationSettings);

    const org = state.organization;
    if (!org?.currencySymbol) {
      // Update currency symbol.

      NumbroUtils.setCurrencySymbol(organizationSettings.currencySymbol);

      Object.keys(numberFormats).forEach((x) => {
        const currencyFormat = numberFormats[x].currency;
        i18n.global.mergeNumberFormat(x, { currency: currencyFormat });
      });
    }

    return organizationSettings;
  },

  async getLatestPlays({ commit }, { params, cancelTokenSource = null, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const latestPlays = await API.instance.getLatestPlays({ params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetLatestPlays');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return latestPlays;
  },

  async orgIsLinkedToDsp({ commit }, { dspCode }) {
    const response = await API.instance.orgIsLinkedToDsp(dspCode);

    return response?.data > 0;
  },
};

export default actions;
